import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { InfoIcon } from '@teamworksdev/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ControllerRenderProps,
  FieldError,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { trpc } from '../../../../trpc/react';
import { Drawer, DrawerFooter, DrawerHeader } from '../../../components/drawer';
import { Form, FormField, FormItem } from '../../../components/form/form';
import { ConfirmationModal } from '../../../components/modals/confirmation-modal';
import { useToast } from '../../../hooks/useToast';
import { AmsClassicConfigInput, Org } from '../types';

/**
 * Schema and defaults
 */

export const zAmsConfigForm = z.object({
  siteUrl: z.string(),
  username: z.string(),
  password: z.string(),
  /** `null` = empty; RHF must not use `undefined` here or clearing reverts to default. */
  amsCricketFormId: z.union([z.number().int().positive(), z.null()]),
});

export type AmsConfigForm = z.infer<typeof zAmsConfigForm>;

const AMS_CONFIG_FORM_ID = 'ams-config-form';

type CricketSyncChangeKind = 'enable' | 'disable' | 'change';

const CRICKET_SYNC_CONFIRM_MESSAGES: Record<CricketSyncChangeKind, string> = {
  enable:
    'The Cricket form id has been added. Saving will enable Cricket sync from AMS for this organization.',
  disable:
    'The Cricket form id has been removed. Saving will disable Cricket sync from AMS for this organization.',
  change:
    'The Cricket form id has been changed. Saving will update Cricket sync in AMS to use the new form id.',
};

function classifyCricketSyncChange(
  prev: number | null,
  next: number | null,
): CricketSyncChangeKind {
  if (prev === null && next !== null) return 'enable';
  if (prev !== null && next === null) return 'disable';
  return 'change';
}

function buildSavePayload(
  values: AmsConfigForm,
  orgId: string,
): AmsClassicConfigInput {
  return {
    orgId,
    config: {
      siteUrl: values.siteUrl,
      username: values.username,
      password: values.password,
      amsCricketFormId:
        values.amsCricketFormId === null ? undefined : values.amsCricketFormId,
    },
  };
}

function syncBaselineAfterSave(
  ref: { current: number | null },
  saved: AmsClassicConfigInput,
) {
  ref.current =
    saved.config.amsCricketFormId === undefined
      ? null
      : saved.config.amsCricketFormId;
}

function useCricketFormIdBaseline(
  open: boolean,
  org: Org,
): { current: number | null } {
  const ref = useRef<number | null>(null);
  const prevOpenRef = useRef(false);
  const snapshotOrgIdRef = useRef(org.id);

  useEffect(() => {
    const opened = open && !prevOpenRef.current;
    const orgSwitched = open && org.id !== snapshotOrgIdRef.current;
    prevOpenRef.current = open;
    if (!opened && !orgSwitched) return;
    snapshotOrgIdRef.current = org.id;
    const id = org.amsClassicConfig?.amsCricketFormId;
    ref.current = id == null ? null : id;
  }, [open, org.id, org.amsClassicConfig?.amsCricketFormId]);

  return ref;
}

export function AmsClassicConfigDrawer({
  open,
  onClose,
  org,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (args: AmsClassicConfigInput) => void | Promise<void>;
  org: Org;
}) {
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [drawerPage, setDrawerPage] = useState<'config' | 'debug'>('config');
  const [cricketModalOpen, setCricketModalOpen] = useState(false);
  const [cricketSyncChangeKind, setCricketSyncChangeKind] =
    useState<CricketSyncChangeKind | null>(null);
  const [pendingSave, setPendingSave] = useState<AmsClassicConfigInput | null>(
    null,
  );

  const originalAmsCricketFormIdRef = useCricketFormIdBaseline(open, org);

  useEffect(() => {
    if (open) setDrawerPage('config');
  }, [open]);

  const { toast } = useToast();
  const { t } = useTranslation();

  const form = useForm<AmsConfigForm>({
    mode: 'onChange',
    resolver: zodResolver(zAmsConfigForm),
    defaultValues: getValuesForAmsConfigForm(org.amsClassicConfig),
  });
  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = form;
  const [password, username, siteUrl] = form.watch([
    'password',
    'username',
    'siteUrl',
  ]);

  const debugConnection =
    trpc.admin.integrations.amsClassic.debugConfig.useMutation();
  const resyncUserMappings =
    trpc.admin.integrations.amsClassic.resyncUserMappings.useMutation({
      onSuccess: () => {
        toast({ title: 'Resync initiated', severity: 'success' });
      },
      onError: (error) => {
        toast({
          title: 'Resync failed',
          description: error.message,
          severity: 'error',
        });
      },
    });
  const validateAmsCredentials =
    trpc.admin.integrations.amsClassic.validateAmsCredentials.useMutation();
  const { reset: resetDebugConnection } = debugConnection;
  const { reset: resetValidateAmsCredentials } = validateAmsCredentials;
  const { reset: resetResyncUserMappings } = resyncUserMappings;

  useEffect(() => {
    resetValidateAmsCredentials();
    resetDebugConnection();
    resetResyncUserMappings();
  }, [
    password,
    username,
    siteUrl,
    resetValidateAmsCredentials,
    resetDebugConnection,
    resetResyncUserMappings,
  ]);

  const runSave = useCallback(
    async (input: AmsClassicConfigInput) => {
      try {
        await Promise.resolve(onSave(input));
        syncBaselineAfterSave(originalAmsCricketFormIdRef, input);
      } catch {
        // Parent surfaces errors (e.g. mutation onError); baseline stays unchanged.
      }
    },
    [onSave, originalAmsCricketFormIdRef],
  );

  const submitAfterValidation: SubmitHandler<AmsConfigForm> = useCallback(
    (values) => {
      const payload = buildSavePayload(values, org.id);
      const prev = originalAmsCricketFormIdRef.current;
      const next = values.amsCricketFormId;
      if (prev === next) {
        runSave(payload);
        return;
      }
      setCricketSyncChangeKind(classifyCricketSyncChange(prev, next));
      setPendingSave(payload);
      setCricketModalOpen(true);
    },
    [org.id, originalAmsCricketFormIdRef, runSave],
  );

  const resetCricketConfirmModal = useCallback(() => {
    setCricketModalOpen(false);
    setPendingSave(null);
    setCricketSyncChangeKind(null);
  }, []);

  const closeHandler = useCallback(() => {
    if (isDirty) setCancelModalOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const handleDebugConnection = useCallback(() => {
    debugConnection.mutate({
      orgId: org.id,
      siteUrl,
      username,
      password,
    });
  }, [debugConnection, org.id, siteUrl, username, password]);

  const handleResyncUserMappings = useCallback(() => {
    resyncUserMappings.mutate({ orgId: org.id });
  }, [resyncUserMappings, org.id]);

  const handleValidateAmsCredentials = useCallback(() => {
    validateAmsCredentials.mutate({
      orgId: org.id,
      siteUrl,
      username,
      password,
    });
  }, [validateAmsCredentials, org.id, siteUrl, username, password]);

  const credValidation = validateAmsCredentials.data;
  const validationFailed = credValidation === false;
  const validationSuccessful = credValidation === true;
  const credentialOkClass = validationSuccessful ? 'border-green-700' : '';

  const isActive = Boolean(
    siteUrl?.trim() && username?.trim() && password?.trim(),
  );

  const cricketSyncChangeMessage = useMemo(
    () =>
      cricketSyncChangeKind
        ? CRICKET_SYNC_CONFIRM_MESSAGES[cricketSyncChangeKind]
        : '',
    [cricketSyncChangeKind],
  );

  const validateButtonClass =
    credValidation === undefined
      ? ''
      : validationSuccessful
        ? 'bg-success-700 text-white'
        : 'bg-red-400 text-white';

  return (
    <Drawer open={open} onClose={closeHandler}>
      <DrawerHeader onClose={closeHandler}>
        <Typography variant="h3">AMS Classic Integration</Typography>
        <Typography variant="body2">{org.name}</Typography>
      </DrawerHeader>
      <Form {...form}>
        <form
          id={AMS_CONFIG_FORM_ID}
          onSubmit={handleSubmit(submitAfterValidation)}
          className="flex min-h-0 flex-1 flex-col gap-4 px-6"
        >
          {drawerPage === 'config' && (
            <div
              id="config-section"
              className="flex min-h-0 flex-1 flex-col gap-4"
            >
              <FormField
                name="siteUrl"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormItem>
                    <TextField
                      {...field}
                      label="URL"
                      fullWidth
                      className={credentialOkClass}
                      error={Boolean(error) || validationFailed}
                      helperText={error?.message}
                    />
                  </FormItem>
                )}
              />

              <div className="flex flex-row gap-4">
                <FormField
                  name="username"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <FormItem className="min-w-0 flex-1">
                      <TextField
                        label="Username"
                        {...field}
                        fullWidth
                        className={credentialOkClass}
                        error={Boolean(error) || validationFailed}
                        helperText={error?.message}
                      />
                    </FormItem>
                  )}
                />
                <FormField
                  name="password"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <FormItem className="min-w-0 flex-1">
                      <TextField
                        label="Password"
                        {...field}
                        fullWidth
                        className={credentialOkClass}
                        error={Boolean(error) || validationFailed}
                        helperText={error?.message}
                        type="password"
                      />
                    </FormItem>
                  )}
                />
              </div>
              <Button
                variant="outlined"
                disabled={validateAmsCredentials.isLoading || !isActive}
                className={validateButtonClass}
                onClick={handleValidateAmsCredentials}
              >
                Validate AMS Credentials
              </Button>

              <FormField
                name="amsCricketFormId"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormItem className="mt-2 flex flex-row flex-wrap items-center gap-2">
                    <CricketFormIdField
                      field={field}
                      error={error}
                      disabled={!isActive}
                    />
                  </FormItem>
                )}
              />
              <div className="mb-4 mt-auto flex flex-row items-center gap-2 pt-2">
                <Button
                  variant="outlined"
                  onClick={handleResyncUserMappings}
                  className="flex-1"
                  disabled={
                    resyncUserMappings.isLoading ||
                    debugConnection.isLoading ||
                    !org.amsClassicConfig
                  }
                >
                  Resync AMS User Mappings
                </Button>
                <Tooltip title="Syncs SC with the axle id user mappings in AMS Classic. This enables those users to sync their strength data to AMS.">
                  <div className="flex">
                    <InfoIcon color="grey" />
                  </div>
                </Tooltip>
              </div>
            </div>
          )}
          {drawerPage === 'debug' && (
            <div id="test-section" className="flex flex-col gap-4">
              <Typography variant="h4">Debug</Typography>

              <Button
                className="mt-4 px-6"
                variant="outlined"
                onClick={handleDebugConnection}
                disabled={debugConnection.isLoading || !isActive}
              >
                Debug Connection to AMS
              </Button>
              {debugConnection.isSuccess &&
                debugConnection.data?.statusCode === 200 && (
                  <Alert severity="success">{t('amsConnectionSuccess')}</Alert>
                )}
              {debugConnection.isSuccess &&
                debugConnection.data &&
                debugConnection.data.statusCode !== 200 && (
                  <MissingAmsMappingsTable debugConnection={debugConnection} />
                )}
              {debugConnection.isError && (
                <Alert severity="error">
                  {'Contact Engineering: ' + debugConnection.error.message}
                </Alert>
              )}
            </div>
          )}
        </form>
      </Form>

      <DrawerFooter className="flex w-full flex-row flex-wrap items-center justify-between gap-2 p-8">
        <div className="flex flex-row gap-2">
          {drawerPage === 'debug' && (
            <Button
              type="button"
              variant="outlined"
              onClick={() => setDrawerPage('config')}
            >
              Back
            </Button>
          )}
          <Button variant="outlined" onClick={closeHandler}>
            Cancel
          </Button>
        </div>
        <div className="flex flex-row gap-2">
          {drawerPage === 'config' && (
            <Button
              type="button"
              variant="outlined"
              onClick={() => setDrawerPage('debug')}
              disabled={!isActive}
            >
              Debug
            </Button>
          )}
          <Button type="submit" form={AMS_CONFIG_FORM_ID} disabled={!isActive}>
            Save
          </Button>
        </div>
      </DrawerFooter>

      <ConfirmationModal
        id="ams-config-cancel"
        open={cancelModalOpen}
        title="Cancel Integration Configuration"
        message="Are you sure you want to cancel configuring the AMS Classic Integration? All changes will be lost."
        onConfirm={() => {
          setCancelModalOpen(false);
          onClose();
        }}
        onCancel={() => setCancelModalOpen(false)}
      />

      <ConfirmationModal
        id="ams-cricket-sync-change"
        open={cricketModalOpen}
        title="Cricket sync change"
        message={cricketSyncChangeMessage}
        confirmText="Save"
        cancelText="Go back"
        onCancel={resetCricketConfirmModal}
        onConfirm={async () => {
          if (pendingSave) await runSave(pendingSave);
          resetCricketConfirmModal();
        }}
      />
    </Drawer>
  );
}

function CricketFormIdField({
  disabled,
  field,
  error,
}: {
  disabled: boolean;
  field: ControllerRenderProps<AmsConfigForm, 'amsCricketFormId'>;
  error?: FieldError;
}) {
  return (
    <TextField
      type="text"
      inputMode="numeric"
      autoComplete="off"
      label="Cricket Form Id"
      size="small"
      sx={{ minWidth: 200, mt: 2 }}
      value={field.value == null ? '' : String(field.value)}
      onChange={(e) => {
        const digitsOnly = e.target.value.replace(/\D/g, '');
        if (digitsOnly === '') {
          field.onChange(null);
          return;
        }
        const n = parseInt(digitsOnly, 10);
        field.onChange(Number.isNaN(n) ? null : n);
      }}
      onBlur={field.onBlur}
      name={field.name}
      inputRef={field.ref}
      inputProps={{
        inputMode: 'numeric',
        pattern: '[0-9]*',
        'aria-invalid': Boolean(error),
      }}
      disabled={disabled}
      error={Boolean(error)}
      helperText={error?.message}
    />
  );
}

type DebugConnectionProps = {
  data?: { statusCode?: number; message: string; context?: unknown };
  isSuccess: boolean;
  isError: boolean;
  error?: { message?: string } | null;
};

function MissingAmsMappingsTable({
  debugConnection,
}: {
  debugConnection: DebugConnectionProps;
}) {
  const data = debugConnection.data;
  if (!data) return null;
  if (data.context === undefined) {
    return <Alert severity="error">{data.message}</Alert>;
  }
  const missing = (
    data.context as {
      missingMappings?: { id: string; profileName: string }[];
    }
  ).missingMappings;
  return (
    <>
      <Alert severity="error">{data.message}</Alert>
      <div className="max-h-[450px] overflow-y-auto border border-1 mb-2">
        {Array.isArray(missing) && (
          <Table className="max-h-[180px] overflow-y-auto">
            <TableHead>
              <TableRow>
                <TableCell>Profile Name</TableCell>
                <TableCell>Axle Id</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {missing.map(({ id, profileName }) => (
                <TableRow key={id}>
                  <TableCell>{profileName}</TableCell>
                  <TableCell>{id}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </>
  );
}

function getValuesForAmsConfigForm(
  config: Org['amsClassicConfig'],
): AmsConfigForm {
  if (!config) {
    return {
      siteUrl: '',
      username: '',
      password: '',
      amsCricketFormId: null,
    };
  }
  return {
    siteUrl: config.amsClassicSiteUrl,
    username: config.amsClassicUsername,
    password: config.amsClassicPassword,
    amsCricketFormId: config.amsCricketFormId ?? null,
  };
}
